import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '../components/ui/alert-dialog';
import {
  X, User, Briefcase, Building2, MapPin, Mail, Phone, Shield,
  Image, FileText, Video, Download, ExternalLink, Hash, Lock, Users,
  LogOut, Trash2, Crown, ShieldPlus, ShieldMinus
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatInfoPanel = ({ channel, onClose, onUserClick, onChannelDeleted, onChannelLeft }) => {
  const { user } = useAuth();
  const { onlineUsers } = useChat();
  const [profileUser, setProfileUser] = useState(null);
  const [sharedMedia, setSharedMedia] = useState([]);
  const [sharedDocs, setSharedDocs] = useState([]);
  const [members, setMembers] = useState([]);
  const [channelInfo, setChannelInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');

  const isDM = !!channel?.dm_id;
  const channelId = channel?.channel_id || channel?.dm_id;
  const otherUser = channel?.other_user;

  // Load channel full info
  useEffect(() => {
    if (isDM || !channel?.channel_id) return;
    axios.get(`${API}/channels/${channel.channel_id}/info`)
      .then(res => setChannelInfo(res.data.channel))
      .catch(() => {});
  }, [isDM, channel]);

  // Load profile for DMs
  useEffect(() => {
    if (!isDM || !otherUser) return;
    axios.get(`${API}/users/${otherUser.user_id}`)
      .then(res => setProfileUser(res.data.user))
      .catch(() => {});
  }, [isDM, otherUser]);

  // Load shared media & docs
  useEffect(() => {
    if (!channelId) return;
    setLoading(true);
    axios.get(`${API}/channels/${channelId}/shared`)
      .then(res => {
        setSharedMedia(res.data.media || []);
        setSharedDocs(res.data.documents || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [channelId]);

  // Load members for channels
  useEffect(() => {
    if (isDM || !channel?.channel_id) return;
    axios.get(`${API}/channels/${channel.channel_id}/members`)
      .then(res => setMembers(res.data.members || []))
      .catch(() => {});
  }, [isDM, channel]);

  const isCreator = channelInfo?.created_by === user?.user_id;
  const isChannelAdmin = channelInfo?.admins?.includes(user?.user_id);
  const canManageAdmins = isCreator || isChannelAdmin;

  const handleDeleteChannel = async () => {
    try {
      await axios.delete(`${API}/channels/${channel.channel_id}`);
      toast.success('Channel deleted');
      onChannelDeleted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete channel');
    }
  };

  const handleLeaveChannel = async () => {
    try {
      await axios.post(`${API}/channels/${channel.channel_id}/leave`);
      toast.success('Left channel');
      onChannelLeft?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to leave channel');
    }
  };

  const handleToggleAdmin = async (userId, isAdmin) => {
    try {
      await axios.post(`${API}/channels/${channel.channel_id}/admins`, {
        user_id: userId,
        action: isAdmin ? 'remove' : 'add'
      });
      toast.success(isAdmin ? 'Admin removed' : 'Admin added');
      // Refresh channel info
      const res = await axios.get(`${API}/channels/${channel.channel_id}/info`);
      setChannelInfo(res.data.channel);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update admin');
    }
  };

  const getAvatarUrl = (u) => {
    if (!u?.avatar) return null;
    return u.avatar.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${u.avatar}` : u.avatar;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const isImageType = (ft) => ft && (ft.startsWith('image/'));
  const isVideoType = (ft) => ft && (ft.startsWith('video/'));

  const displayUser = profileUser || otherUser;
  const avatarUrl = getAvatarUrl(displayUser);
  const isOnline = otherUser ? onlineUsers[otherUser.user_id] : false;

  return (
    <div className="h-full flex flex-col bg-card border-l border-border" data-testid="chat-info-panel">
      {/* Header */}
      <div className="h-14 flex items-center justify-between px-4 border-b border-border flex-shrink-0">
        <h3 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
          {isDM ? 'Profile' : 'Channel Info'}
        </h3>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} data-testid="close-info-panel">
          <X className="w-4 h-4" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4">
          {/* Profile / Channel Header */}
          {isDM ? (
            <div className="text-center mb-4" data-testid="dm-profile-info">
              <div className="relative inline-block mb-3">
                <div className="w-20 h-20 rounded-full bg-[hsl(var(--primary)/0.15)] flex items-center justify-center text-3xl font-bold text-[hsl(var(--primary))] mx-auto overflow-hidden">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    displayUser?.display_name?.[0] || displayUser?.username?.[0] || '?'
                  )}
                </div>
                <div className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-card ${isOnline ? 'bg-[hsl(var(--presence-online))]' : 'bg-[hsl(var(--surface-3))]'}`} />
              </div>
              <h4 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
                {displayUser?.display_name || displayUser?.username || 'User'}
              </h4>
              <p className="text-sm text-muted-foreground">@{displayUser?.username}</p>
              <div className="flex items-center justify-center gap-2 mt-1.5">
                <span className={`text-xs ${isOnline ? 'text-[hsl(var(--presence-online))]' : 'text-muted-foreground'}`}>
                  {isOnline ? 'Online' : 'Offline'}
                </span>
                {displayUser?.system_role === 'admin' && (
                  <Badge className="bg-[hsl(var(--primary))] text-white border-0 text-[10px] h-5">
                    <Shield className="w-2.5 h-2.5 mr-0.5" /> Admin
                  </Badge>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center mb-4" data-testid="channel-info-header">
              <div className="w-16 h-16 rounded-2xl bg-[hsl(var(--primary)/0.10)] flex items-center justify-center mx-auto mb-3">
                {channel?.is_private ? (
                  <Lock className="w-7 h-7 text-[hsl(var(--primary))]" />
                ) : (
                  <Hash className="w-7 h-7 text-[hsl(var(--primary))]" />
                )}
              </div>
              <h4 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
                #{channel?.name}
              </h4>
              {channel?.description && (
                <p className="text-sm text-muted-foreground mt-1">{channel.description}</p>
              )}
              <div className="flex items-center justify-center gap-1.5 mt-2 text-xs text-muted-foreground">
                <Users className="w-3 h-3" />
                <span>{members.length} member{members.length !== 1 ? 's' : ''}</span>
              </div>

              {/* Channel governance actions */}
              <div className="flex items-center justify-center gap-2 mt-3" data-testid="channel-actions">
                {!isCreator && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="text-xs gap-1.5" data-testid="leave-channel-btn">
                        <LogOut className="w-3.5 h-3.5" /> Leave
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Leave #{channel?.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          You will no longer receive messages from this channel. You can rejoin later if needed.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleLeaveChannel} data-testid="confirm-leave-btn">Leave Channel</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                {isCreator && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive" size="sm" className="text-xs gap-1.5" data-testid="delete-channel-btn">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete #{channel?.name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete the channel and all its messages. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteChannel} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-channel-btn">
                          Delete Channel
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
              {isCreator && (
                <p className="text-[10px] text-muted-foreground mt-1.5 flex items-center justify-center gap-1">
                  <Crown className="w-3 h-3" /> You created this channel
                </p>
              )}
            </div>
          )}

          <Separator className="mb-3" />

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full grid grid-cols-3 h-9 bg-[hsl(var(--surface-2))]" data-testid="info-panel-tabs">
              <TabsTrigger value="info" className="text-xs" data-testid="info-tab">
                {isDM ? 'Details' : 'Members'}
              </TabsTrigger>
              <TabsTrigger value="media" className="text-xs" data-testid="media-tab">
                Media
              </TabsTrigger>
              <TabsTrigger value="docs" className="text-xs" data-testid="docs-tab">
                Files
              </TabsTrigger>
            </TabsList>

            {/* Info / Details Tab */}
            <TabsContent value="info" className="mt-3">
              {isDM && displayUser ? (
                <div className="space-y-3" data-testid="user-details-tab">
                  {displayUser.title && (
                    <div className="flex items-center gap-3 text-sm">
                      <Briefcase className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Title</p>
                        <p className="font-medium">{displayUser.title}</p>
                      </div>
                    </div>
                  )}
                  {displayUser.department && (
                    <div className="flex items-center gap-3 text-sm">
                      <Building2 className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Department</p>
                        <p className="font-medium">{displayUser.department}</p>
                      </div>
                    </div>
                  )}
                  {displayUser.location && (
                    <div className="flex items-center gap-3 text-sm">
                      <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Location</p>
                        <p className="font-medium">{displayUser.location}</p>
                      </div>
                    </div>
                  )}
                  {displayUser.email && (
                    <div className="flex items-center gap-3 text-sm">
                      <Mail className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="font-medium text-[hsl(var(--primary))]">{displayUser.email}</p>
                      </div>
                    </div>
                  )}
                  {displayUser.phone && (
                    <div className="flex items-center gap-3 text-sm">
                      <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div>
                        <p className="text-xs text-muted-foreground">Phone</p>
                        <p className="font-medium">{displayUser.phone}</p>
                      </div>
                    </div>
                  )}
                  {displayUser.bio && (
                    <div className="pt-2">
                      <p className="text-xs text-muted-foreground mb-1">About</p>
                      <p className="text-sm leading-relaxed">{displayUser.bio}</p>
                    </div>
                  )}
                  {!displayUser.title && !displayUser.department && !displayUser.location && !displayUser.email && !displayUser.phone && !displayUser.bio && (
                    <div className="text-center py-6">
                      <User className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-40" />
                      <p className="text-sm text-muted-foreground">No profile details available</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-1" data-testid="channel-members-tab">
                  {members.length > 0 ? members.map(m => {
                    const isMemberAdmin = channelInfo?.admins?.includes(m.user_id);
                    const isMemberCreator = channelInfo?.created_by === m.user_id;
                    return (
                      <div key={m.user_id} className="flex items-center gap-2.5 px-2 py-2 rounded-lg text-sm hover:bg-[hsl(var(--message-hover))] transition-colors group" data-testid={`member-item-${m.user_id}`}>
                        <button className="flex items-center gap-2.5 flex-1 min-w-0" onClick={() => onUserClick?.(m.user_id)}>
                          <div className="relative flex-shrink-0">
                            <div className="w-7 h-7 rounded-full bg-[hsl(var(--surface-3))] flex items-center justify-center text-xs font-semibold">
                              {m.display_name?.[0] || m.username?.[0] || '?'}
                            </div>
                            <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-card ${onlineUsers[m.user_id] ? 'bg-[hsl(var(--presence-online))]' : 'bg-[hsl(var(--surface-3))]'}`} />
                          </div>
                          <span className="flex-1 text-left truncate">{m.display_name || m.username}</span>
                        </button>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {isMemberCreator && (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] h-4 gap-0.5"><Crown className="w-2.5 h-2.5" />Owner</Badge>
                          )}
                          {isMemberAdmin && !isMemberCreator && (
                            <Badge variant="secondary" className="text-[10px] h-4 gap-0.5"><Shield className="w-2.5 h-2.5" />Admin</Badge>
                          )}
                          {canManageAdmins && !isMemberCreator && m.user_id !== user?.user_id && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => handleToggleAdmin(m.user_id, isMemberAdmin)}
                              data-testid={`toggle-admin-${m.user_id}`}
                            >
                              {isMemberAdmin ? <ShieldMinus className="w-3.5 h-3.5 text-muted-foreground" /> : <ShieldPlus className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  }) : (
                    <div className="text-center py-6">
                      <Users className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-40" />
                      <p className="text-sm text-muted-foreground">No members found</p>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            {/* Media Tab */}
            <TabsContent value="media" className="mt-3">
              {loading ? (
                <div className="grid grid-cols-3 gap-1.5">
                  {[1, 2, 3, 4, 5, 6].map(i => (
                    <Skeleton key={i} className="aspect-square rounded-lg" />
                  ))}
                </div>
              ) : sharedMedia.length > 0 ? (
                <div data-testid="shared-media-grid">
                  <p className="text-xs text-muted-foreground mb-2">{sharedMedia.length} item{sharedMedia.length !== 1 ? 's' : ''}</p>
                  <div className="grid grid-cols-3 gap-1.5">
                    {sharedMedia.map((item, i) => (
                      <a key={i} href={item.url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${item.url}` : item.url}
                        target="_blank" rel="noopener noreferrer"
                        className="aspect-square rounded-lg overflow-hidden bg-[hsl(var(--surface-2))] hover:opacity-80 transition-opacity relative group"
                        data-testid={`media-item-${i}`}>
                        {isImageType(item.file_type) ? (
                          <img src={item.url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${item.url}` : item.url} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Video className="w-6 h-6 text-muted-foreground" /></div>
                        )}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <ExternalLink className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8" data-testid="no-media-state">
                  <Image className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" />
                  <p className="text-sm text-muted-foreground">No shared media yet</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Photos and videos shared here will appear</p>
                </div>
              )}
            </TabsContent>

            {/* Files Tab */}
            <TabsContent value="docs" className="mt-3">
              {loading ? (
                <div className="space-y-2">{[1, 2, 3].map(i => (<Skeleton key={i} className="h-14 rounded-lg" />))}</div>
              ) : sharedDocs.length > 0 ? (
                <div className="space-y-1" data-testid="shared-docs-list">
                  <p className="text-xs text-muted-foreground mb-2">{sharedDocs.length} file{sharedDocs.length !== 1 ? 's' : ''}</p>
                  {sharedDocs.map((doc, i) => (
                    <a key={i} href={doc.url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${doc.url}` : doc.url}
                      target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[hsl(var(--message-hover))] transition-colors group"
                      data-testid={`doc-item-${i}`}>
                      <div className="w-9 h-9 rounded-lg bg-[hsl(var(--primary)/0.10)] flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-[hsl(var(--primary))]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{doc.name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {doc.size > 0 && <span>{formatFileSize(doc.size)}</span>}
                          <span>{formatDate(doc.timestamp)}</span>
                          <span>{doc.sender_name}</span>
                        </div>
                      </div>
                      <Download className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    </a>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8" data-testid="no-docs-state">
                  <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-2 opacity-30" />
                  <p className="text-sm text-muted-foreground">No shared files yet</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Documents shared here will appear</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </ScrollArea>
    </div>
  );
};

export default ChatInfoPanel;
